import { ALL_RESTAURANTS_ID } from '../../../../../stores/appUserDefaultStore';
import { AskCustomerButton } from '../../../../customerRequests/CustomerRequestComposer';
import { usePracticeScope } from '../../../../onboarding/practice/practiceScope';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableComponent } from '../../../../ui/TableComponent';
import { UnmappedProductRow, UtilizationTab } from '../../../../../utils/reports/utilizationReport';
import OverOrderingChart from './OverOrderingChart';
import { useChartAlignment } from './useChartAlignment';
import { Platform } from '../../../../../types/Platforms';
import { SupplierLogo } from '../../../../ui/SupplierLogo';
import { Calculator, MoreVertical, Sparkles } from 'lucide-react';
import { getCategoryStyles } from '../../../../orderhistory/categoryUtils';
import { formatCategoryName } from '../../../../orderhistory/sections/FilterControls';
import { eur } from '../../../../orderhistory/utils';
import { useAppSettingsStore } from '../../../../../stores/appSettingsStore';
import LinkAlternativesBulkModal from '../../../costPrice/new/CostPrice/LinkAlternativesBulkModal';

interface PurchasedNotLinkedTableProps {
  restaurantId?: string;
  /** Already scoped by the page's shared filters — this table does no filtering. */
  rows: UnmappedProductRow[];
  totalSpend: number;
  /** When set, a trailing "Setup" action opens the product-setup flow for the row. */
  onSetup?: (row: UnmappedProductRow) => void;
  onRowClick?: (row: UnmappedProductRow) => void;
  /** The page's tab — decides what the left-hand chart's bars measure. */
  chartTab: UtilizationTab;
  canCheckAlternatives?: boolean;
  defaultWastePercentage?: number;
}

/**
 * Products bought in the period that no cost-price recipe uses — on the shared app
 * `TableComponent` (spend sorting, concise pagination). Only rendered on the
 * Not-linked tab, whose search / supplier / category filters live in
 * `UtilizationControls` with every other filter on the page.
 * Explicit alternative selection operates on these filtered, unlinked rows only.
 */
export const PurchasedNotLinkedTable: React.FC<PurchasedNotLinkedTableProps> = ({ rows, totalSpend, onSetup, onRowClick, chartTab, restaurantId, canCheckAlternatives = false, defaultWastePercentage = 0 }) => {
  const { t } = useTranslation();
  const practice = usePracticeScope();
  const darkMode = useAppSettingsStore((s) => s.darkMode);
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | null>('desc');
  const [sortCol, setSortCol] = useState<string | null>('spend');
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [reviewProducts, setReviewProducts] = useState<UnmappedProductRow[]>([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const menu = useRef<HTMLDivElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const allowed = canCheckAlternatives && !practice && chartTab === 'unmapped' && !!restaurantId && restaurantId !== ALL_RESTAURANTS_ID;
  const selecting = allowed && selectionMode;
  const selectedRows = rows.filter(row => selectedKeys.has(row.key));
  const reviewKeys = useMemo(() => reviewProducts.map(row => row.key), [reviewProducts]);
  const reviewNames = useMemo(() => reviewProducts.map(row => row.name), [reviewProducts]);
  const label = (key: string, options?: Record<string, unknown>) => t(`reports.utilization.alternatives.${key}`, options);

  // Pagination is local to TableComponent. Filter changes remove hidden selections;
  // a running review retains the explicit product snapshot it started with.
  useEffect(() => {
    const visible = new Set(rows.map(row => row.key));
    setSelectedKeys(previous => {
      const next = new Set(Array.from(previous).filter(key => visible.has(key)));
      return next.size === previous.size ? previous : next;
    });
  }, [rows]);
  useEffect(() => {
    if (!allowed) { setMenuOpen(false); setSelectionMode(false); setSelectedKeys(new Set()); setReviewOpen(false); setReviewProducts([]); }
  }, [allowed]);
  useEffect(() => {
    if (!menuOpen) return;
    menu.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    const outside = (event: MouseEvent) => { if (!menu.current?.contains(event.target as Node)) setMenuOpen(false); };
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setMenuOpen(false); menuButton.current?.focus(); }
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', keydown);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', keydown); };
  }, [menuOpen]);

  const toggle = (key: string) => setSelectedKeys(previous => {
    const next = new Set(previous);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const view = useMemo(() => {
    if (sortCol !== 'spend' || !sortDir) return rows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => (a.spend - b.spend) * dir);
  }, [rows, sortCol, sortDir]);

  const { wrapRef, onPageChange, sliceStart, sliceEnd, alignment } = useChartAlignment(
    `${view.length}|${sortCol}|${sortDir}`
  );

  const handleSort = (column: string) => {
    if (sortCol === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : sortDir === 'desc' ? null : 'asc');
      if (sortDir === 'desc') setSortCol(null);
    } else {
      setSortCol(column);
      setSortDir('asc');
    }
  };

  const columns: any[] = [
    {
      key: 'name',
      label: t('reports.utilization.col.product'),
      render: (_v: any, r: UnmappedProductRow) => selecting ? (
        <label className="flex min-h-11 items-center gap-3 text-sm font-semibold text-textPrimary dark:text-textPrimary-dark" onClick={event => event.stopPropagation()}>
          <input type="checkbox" checked={selectedKeys.has(r.key)} onChange={() => toggle(r.key)} className="h-4 w-4 shrink-0" />
          <span>{r.name}</span>
        </label>
      ) : <span className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark">{r.name}</span>,
    },
    {
      key: 'supplier',
      label: t('reports.utilization.col.supplier'),
      render: (_v: any, r: UnmappedProductRow) => {
        const platform = Platform.fromString(r.platform);
        return <div className="flex justify-center items-center" title={r.supplier}><SupplierLogo platform={platform} unknownSupplierName={r.unknownSupplierName} alt={r.supplier} size="small" /></div>;
      },
    },
    {
      key: 'category',
      label: t('reports.utilization.col.category'),
      render: (_v: any, r: UnmappedProductRow) =>
        r.category ? (
          <span className="text-xs font-medium rounded-md px-2 py-1 whitespace-nowrap" style={getCategoryStyles(r.category, darkMode)}>{formatCategoryName(r.category)}</span>
        ) : (
          <span className="text-muted dark:text-muted-dark">—</span>
        ),
    },
    {
      key: 'spend',
      label: t('reports.utilization.col.spend'),
      sortable: true,
      sortKey: 'spend',
      render: (_v: any, r: UnmappedProductRow) => <span className="font-medium text-textPrimary dark:text-textPrimary-dark">{eur(r.spend)}</span>,
    },
    ...(onSetup
      ? [
          {
            key: 'setup',
            label: t('inventory.colActions'),
            render: (_v: any, r: UnmappedProductRow) => (
              <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-onboarding={practice ? "utilization-setup" : undefined}
                onClick={() => onSetup(r)}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-borderGrey dark:border-borderGrey-dark text-sm font-semibold text-[#0E8965] dark:text-darkGreenColor-dark hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <Calculator className="w-3.5 h-3.5" />
                {t('inventory.setupAction')}
              </button>
              {!practice && restaurantId && restaurantId !== ALL_RESTAURANTS_ID && <AskCustomerButton seed={{ restaurantId, type: 'ingredient_dish_usage', mode: 'add_missing', productName: r.name }} />}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="h-full min-w-0 flex flex-col gap-3">
      {/* Title in its own card so the table card below starts with its `thead` — that's
          what the chart's title band lines up against. */}
      <div className="rounded-xl bg-background dark:bg-background-dark shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040] p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="text-lg font-semibold text-textPrimary dark:text-textPrimary-dark">
            {`${t('reports.utilization.unmapped.title')} · ${t('reports.utilization.group.summary', { count: rows.length, spend: eur(totalSpend) })}`}
          </div>
          {allowed && !selecting && <div ref={menu} className="relative shrink-0">
            <button ref={menuButton} type="button" aria-label={t('products.actions.menuLabel')} aria-haspopup="menu" aria-expanded={menuOpen}
              onClick={() => setMenuOpen(open => !open)}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark bg-white dark:bg-background-dark text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors">
              <MoreVertical className="h-5 w-5" />
            </button>
            {menuOpen && <div role="menu" className="absolute right-0 top-full z-50 mt-1 min-w-[240px] rounded-xl border border-border dark:border-border-dark bg-white dark:bg-background-dark py-1 shadow-lg">
              <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setSelectionMode(true); }}
                className="flex min-h-11 w-full items-center gap-2 px-4 py-2 text-left text-sm text-textPrimary dark:text-textPrimary-dark hover:bg-gray-50 dark:hover:bg-gray-800">
                <Sparkles className="h-4 w-4 shrink-0" />{label('check')}
              </button>
            </div>}
          </div>}
        </div>
        <p className="mt-1 text-xs text-muted dark:text-muted-dark">{t('reports.utilization.unmapped.alternativeNote')}</p>
        {selecting && <div className="mt-3 flex flex-wrap items-center gap-3">
          <span role="status" className="text-sm text-textPrimary dark:text-textPrimary-dark">{label('selected', { count: selectedRows.length })}</span>
          <button type="button" onClick={() => setSelectedKeys(new Set(rows.map(row => row.key)))} className="min-h-11 text-sm text-[#0E8965] dark:text-darkGreenColor-dark">{label('selectAll')}</button>
          <button type="button" onClick={() => { setSelectionMode(false); setSelectedKeys(new Set()); }} className="min-h-11 text-sm text-muted dark:text-muted-dark">{label('cancel')}</button>
          <button type="button" disabled={!selectedRows.length} onClick={() => { setReviewProducts(selectedRows); setReviewOpen(true); }}
            className="min-h-11 rounded-xl bg-[#0E8965] dark:bg-darkGreenColor-dark px-4 py-2 text-sm font-semibold text-white dark:text-white disabled:opacity-50">
            {label('link')}
          </button>
        </div>}
      </div>

      <div className="flex flex-col-reverse gap-5 lg:grid lg:grid-cols-[minmax(280px,420px)_1fr] lg:items-stretch">
        <OverOrderingChart
          rows={[]}
          unmappedRows={view.slice(sliceStart, sliceEnd)}
          tab={chartTab}
          align={alignment}
        />

        <div ref={wrapRef} className="min-w-0 w-full h-full flex flex-col">
          <TableComponent<UnmappedProductRow>
            title=""
            data={view}
            onRowClick={onRowClick}
            columns={columns}
            blankLines={false}
            usePagination
            alignOtherColumnsRight={false}
            onPageChange={onPageChange}
            sortColumn={sortCol}
            sortDirection={sortDir}
            onSort={handleSort}
            className="flex-1 min-h-0"
          />
        </div>
      </div>
      {allowed && restaurantId && reviewProducts.length > 0 && <LinkAlternativesBulkModal
        isOpen={reviewOpen} onClose={() => setReviewOpen(false)} restaurantId={restaurantId}
        defaultWastePercentage={defaultWastePercentage} selectedProductKeys={reviewKeys} selectedProductNames={reviewNames}
      />}
    </div>
  );
};

export default PurchasedNotLinkedTable;
